import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AppRoutes from "./routes/AppRoutes";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import "./styles/global.css";
import { NotificationData } from "./types/types";
import useAuthService from "./auth/AuthService";
import { initGA, trackPageView, trackScrollDepth } from "./utils/ga";
import { initMetaPixel } from "./utils/metaPixel";
import {
  AddUserProfile,
  fetchUserProfile,
} from "./services/external.api.service";

const App: React.FC = () => {
  const { isAuthenticated, isLoading, getToken, getUser } = useAuthService();
  const location = useLocation();

  const [superState, setSuperState] = useState({
    role: "",
    profileImageUrl: "",
    deleteCartItemId: "",
    cartOpen: false,
    isNewNotification: false,
    notifications: [] as any[],
    isFocusSearch: false,
  });

  const prevNotificationsRef = useRef<NotificationData[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    initGA();
    trackScrollDepth();
    initMetaPixel();
  }, []);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    trackPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAuthenticated) {
          const userProfileData = await fetchUserProfile(getToken);
          if (userProfileData.data === "") {
            const user: any = getUser();
            const userData: any = {
              email: user.email || "",
              is_added: true,
            };
            await AddUserProfile(userData, getToken);
          }
        }
      } catch (err) {
        console.error("Error in fetching user data: ", err);
      }
    };

    fetchData();
    //eslint-disable-next-line
  }, [isAuthenticated]);

  useEffect(() => {
    const connectWebSocket = () => {
      if (isAuthenticated) {
        const user = getUser();
        if (!user?.sub) return console.error("User not authenticated");

        if (!socketRef.current) {
          socketRef.current = new WebSocket(
            `${process.env.REACT_APP_FAN1_WEBSOCKET_URL}/api/notification/get-notifications?id=${user.sub}`
          );
        }

        socketRef.current.onmessage = (event) => {
          try {
            const notificationsData: NotificationData[] = JSON.parse(
              event.data
            );
            if (notificationsData === null) {
              setSuperState((prev) => ({
                ...prev,
                notifications: [],
                isNewNotification: false,
              }));
            } else {
              const isDifferent =
                prevNotificationsRef?.current?.length !==
                  notificationsData?.length ||
                JSON.stringify(prevNotificationsRef.current) !==
                  JSON.stringify(notificationsData || []);

              if (isDifferent) {
                const hasUnreadNotifications = notificationsData?.some(
                  (n) => !n.is_read
                );
                prevNotificationsRef.current = notificationsData;

                setSuperState((prev) => ({
                  ...prev,
                  notifications: notificationsData || [],
                  isNewNotification: hasUnreadNotifications,
                }));
              }
            }
          } catch (error) {
            console.error("WebSocket parse error:", error);
          }
        };

        socketRef.current.onerror = () => {
          console.error("WebSocket error. Reconnecting...");
          socketRef.current?.close();
          socketRef.current = null;
          if (!reconnectTimeoutRef.current) {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
          }
        };

        socketRef.current.onclose = () => {
          if (!reconnectTimeoutRef.current) {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
          }
        };
      }
    };

    connectWebSocket();

    return () => {
      socketRef.current?.close();
      socketRef.current = null;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, getUser]);

  const handleUserRole = (role: string) => {
    setSuperState((prev) => ({ ...prev, role }));
  };

  const handleImageChange = (newImageUrl: string) => {
    setSuperState((prev) => ({ ...prev, profileImageUrl: newImageUrl }));
  };

  const handleDeletionInCart = (cartItem: string) => {
    setSuperState((prev) => ({ ...prev, deleteCartItemId: cartItem }));
  };

  const handleScrollToTop = () => {
    boxRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    setSuperState((prev) => ({ ...prev, isFocusSearch: !prev.isFocusSearch }));
  };

  const isHomePage =
    location.pathname === "/" || location.pathname === "/exclusive-membership";

  // ✅ BLOCK rendering until Auth0 finishes loading
  if (isLoading) return null;

  return (
    <div className="App" ref={boxRef}>
      <Header
        profileImageUrl={superState.profileImageUrl}
        handleImageChange={handleImageChange}
        handleDeletionInCart={handleDeletionInCart}
        handleCartOpen={() =>
          setSuperState((prev) => ({ ...prev, cartOpen: false }))
        }
        setCartOpen={superState.cartOpen}
        handleUserRole={handleUserRole}
        isNewNotification={superState.isNewNotification}
        isFocusSearch={superState.isFocusSearch}
      />
      <main style={{ position: "relative" }}>
        <AppRoutes
          userRole={superState.role}
          handleImageChange={handleImageChange}
          notifications={superState.notifications}
          handleScrollToTop={handleScrollToTop}
        />
        <div
          className={isHomePage ? "center-white-ellipse" : "side-white-ellipse"}
        />
      </main>
      <Footer />
    </div>
  );
};

export default App;
